import React from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import type { OperatorValue } from '@atlaskit/jql-ast/ast/operator';
import {
	COMPARISON_OPERATORS,
	OPERATOR_EQUALS,
	OPERATOR_IN,
	OPERATOR_IS_NOT,
	OPERATOR_NOT_EQUALS,
	OPERATOR_NOT_IN,
	OPERATOR_NOT_LIKE,
	OPERATOR_WAS_NOT,
	OPERATOR_WAS_NOT_IN,
} from '@atlaskit/jql-ast/operator';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag/removable-tag';
import type { JiraViewResolvedField } from '@atlassian/conversation-assistant-message-actions-api/jira';

import fieldLabelMessages from './messages';
import type { ParsedJqlClause, ParsedJqlPredicate, ParsedJqlSort } from './parseViewJql';

const styles = cssMap({
	fieldGroup: {
		flexShrink: 0,
	},
});

const NEGATED_OPERATORS = new Set<OperatorValue>([
	OPERATOR_NOT_EQUALS,
	OPERATOR_NOT_IN,
	OPERATOR_NOT_LIKE,
	OPERATOR_IS_NOT,
	OPERATOR_WAS_NOT,
	OPERATOR_WAS_NOT_IN,
]);

const FIELD_LABEL_MESSAGES: Record<string, MessageDescriptor> = fieldLabelMessages;

const resolveFieldLabel = (
	fieldKey: string,
	formatMessage: (message: MessageDescriptor) => string,
	fields?: JiraViewResolvedField[],
): string => {
	const message = FIELD_LABEL_MESSAGES[fieldKey.toLowerCase()];
	if (message) {
		return formatMessage(message);
	}
	const customField = fields?.find(
		(field) => field.jqlClause.toLowerCase() === fieldKey.toLowerCase(),
	);
	if (customField) {
		return customField.fieldName;
	}
	return fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1);
};

const groupClausesByField = (
	clauses: ParsedJqlClause[],
	formatMessage: (message: MessageDescriptor) => string,
	fields?: JiraViewResolvedField[],
): ParsedJqlClause[][] => {
	const fieldOrder: string[] = [];
	const clausesByField = new Map<string, ParsedJqlClause[]>();

	for (const clause of clauses) {
		// Group by resolved display label, not the raw JQL field key, so aliased fields that
		// resolve to the same label (e.g. "type"/"issuetype" both -> "Work type") render as one
		// chip group instead of two identically-labeled ones.
		const fieldKey = resolveFieldLabel(clause.field, formatMessage, fields);
		if (!clausesByField.has(fieldKey)) {
			fieldOrder.push(fieldKey);
			clausesByField.set(fieldKey, []);
		}
		clausesByField.get(fieldKey)!.push(clause);
	}

	return fieldOrder.map((field) => clausesByField.get(field)!);
};

const formatPredicates = (
	predicates: ParsedJqlPredicate[] | undefined,
	resolveValueText: (value: string) => string,
): string => {
	if (!predicates || predicates.length === 0) return '';
	return (
		' ' +
		predicates
			.map((predicate) =>
				`${predicate.operator} ${predicate.value.map(resolveValueText).join(', ')}`.trim(),
			)
			.join(' ')
	);
};

type ViewFilterChipsProps = {
	clauses: ParsedJqlClause[];
	fields?: JiraViewResolvedField[];
	hydratedUserNamesById?: Map<string, string>;
};

export const ViewFilterChips = ({
	clauses,
	fields,
	hydratedUserNamesById,
}: ViewFilterChipsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const resolveValueText = (value: string): string => hydratedUserNamesById?.get(value) ?? value;

	if (clauses.length === 0) {
		return null;
	}

	return (
		<Inline space="space.100" rowSpace="space.100" shouldWrap>
			{groupClausesByField(clauses, formatMessage, fields).map((fieldClauses) => {
				const { field } = fieldClauses[0];
				const fieldLabel = resolveFieldLabel(field, formatMessage, fields);

				return (
					<Inline
						key={field}
						space="space.050"
						alignBlock="center"
						shouldWrap
						xcss={styles.fieldGroup}
					>
						<Text color="color.text.subtle" size="small">
							{fieldLabel}
						</Text>
						<Inline space="space.050" shouldWrap role="group" aria-label={fieldLabel}>
							{fieldClauses.map((clause, index) => {
								if (clause.rawText) {
									return (
										<Tag
											key={`raw-${index}`}
											text={clause.rawText}
											isRemovable={false}
											color="standard"
											hasMargin={false}
										/>
									);
								}

								if (clause.operator === OPERATOR_EQUALS || clause.operator === OPERATOR_IN) {
									return clause.value.map((value) => (
										<Tag
											key={`${index}-${value}`}
											text={resolveValueText(value)}
											isRemovable={false}
											color="standard"
											hasMargin={false}
										/>
									));
								}

								if (
									NEGATED_OPERATORS.has(clause.operator) ||
									(COMPARISON_OPERATORS as readonly string[]).includes(clause.operator)
								) {
									return [
										<Tag
											key={`${clause.operator}-operator-${index}`}
											text={clause.operator}
											isRemovable={false}
											color="standard"
											hasMargin={false}
										/>,
										<Tag
											key={`${clause.operator}-${index}-${clause.value.join(',')}`}
											text={`${clause.value.map(resolveValueText).join(', ')}${formatPredicates(clause.predicates, resolveValueText)}`}
											isRemovable={false}
											color="standard"
											hasMargin={false}
										/>,
									];
								}

								return [
									<Tag
										key={`${clause.operator}-${index}-${clause.value.join(',')}`}
										text={`${clause.operator} ${clause.value.map(resolveValueText).join(', ')}${formatPredicates(clause.predicates, resolveValueText)}`}
										isRemovable={false}
										color="standard"
										hasMargin={false}
									/>,
								];
							})}
						</Inline>
					</Inline>
				);
			})}
		</Inline>
	);
};

type ViewGroupChipProps = {
	groupBy: string;
	fields?: JiraViewResolvedField[];
};

export const ViewGroupChip = ({ groupBy, fields }: ViewGroupChipProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline>
			<Tag
				text={resolveFieldLabel(groupBy, formatMessage, fields)}
				isRemovable={false}
				color="standard"
				hasMargin={false}
			/>
		</Inline>
	);
};

type ViewSortChipsProps = {
	sorts: ParsedJqlSort[];
	fields?: JiraViewResolvedField[];
};

export const ViewSortChips = ({ sorts, fields }: ViewSortChipsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.100" rowSpace="space.100" shouldWrap>
			{sorts.map((sort, index) => (
				<Tag
					key={`${sort.field}-${sort.direction}-${index}`}
					text={`${resolveFieldLabel(sort.field, formatMessage, fields)} ${sort.direction}`}
					isRemovable={false}
					color="standard"
					hasMargin={false}
				/>
			))}
		</Inline>
	);
};
